/**
 * Axios Interceptor Utility
 * Handles Authorization header injection and 401 error handling with token refresh.
 * On 401: calls VITE_REACT_REFRESH_URL/v1/auth/token/refresh, queues concurrent requests,
 * then retries. Redirects to login after MAX_REFRESH_ATTEMPTS failed attempts.
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { v4 as uuidv4 } from "uuid";
import { getAccessAndRefreshToken, updatePersistUserDbAuthTokens } from "./authStorage";
import { getLocaleFromStorage, DEFAULT_LOCALE } from "../locale";
import { store } from "../../store";
import { SET_AUTH_DATA } from "../../db/actionTypes/otpValidation";

const REFRESH_PATH = "/v1/auth/token/refresh";
const MAX_REFRESH_ATTEMPTS = 2;

const getRefreshBaseUrl = (): string =>
  ((import.meta.env.VITE_REACT_REFRESH_URL as string) || 'http://login.impact0mics.local:9003').replace(/\/$/, '');

// ─── Debug helpers ────────────────────────────────────────────────────────────

const decodeTokenInfo = (token: string): Record<string, unknown> => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return { error: 'malformed — no payload segment' };
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    const now = Math.floor(Date.now() / 1000);
    const exp: number | undefined = decoded.exp;
    return {
      sub: decoded.sub,
      roles: decoded.roles ?? decoded.role ?? decoded.authorities,
      iat: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : undefined,
      exp: exp ? new Date(exp * 1000).toISOString() : undefined,
      expiresInSeconds: exp ? exp - now : undefined,
      isExpired: exp ? exp < now : 'unknown',
      tokenPrefix: `${token.slice(0, 20)}…`,
    };
  } catch {
    return { error: 'failed to decode JWT' };
  }
};

// ─── Redirect ────────────────────────────────────────────────────────────────

/** Auth-page path segments — never used as a returnUrl. */
const AUTH_PATHS = ["/login", "/otp", "/signup", "/forgot-password", "/reset-password"];

const redirectToLogin = (): void => {
  const locale = getLocaleFromStorage() || DEFAULT_LOCALE;
  let returnUrlParam = "";

  if (typeof window !== "undefined") {
    const currentPath = window.location.pathname + window.location.search;
    const isAuthPage = AUTH_PATHS.some((p) => currentPath.includes(p));
    if (!isAuthPage && currentPath !== "/") {
      returnUrlParam = `?returnUrl=${encodeURIComponent(currentPath)}`;
    }
  }

  console.warn(`[AUTH] Redirecting to login: /user/${locale}/login${returnUrlParam}`);
  if (typeof window !== "undefined") {
    // Use replace so the current page is NOT added to browser history —
    // pressing Back after re-login won't cycle through IAM pages.
    window.location.replace(`/user/${locale}/login${returnUrlParam}`);
  }
};

// ─── Token refresh ───────────────────────────────────────────────────────────

const refreshAccessToken = async (): Promise<string> => {
  const baseURL = getRefreshBaseUrl();
  if (!baseURL) {
    throw new Error('VITE_REACT_REFRESH_URL is not configured');
  }

  const { refreshToken, sessionId } = getAccessAndRefreshToken();
  if (!refreshToken || refreshToken.trim() === '') {
    throw new Error('No refresh token available');
  }

  if (!sessionId || sessionId.trim() === '') {
    console.warn('[AUTH] sessionId missing — cannot refresh token');
    throw new Error('sessionId is required for token refresh but was not found in store');
  }

  const refreshUrl = `${baseURL}${REFRESH_PATH}`;
  console.log(`%c[AUTH] Calling refresh endpoint: ${refreshUrl}`, 'color:#e6a817;font-weight:bold');

  const refreshAxios = axios.create({ timeout: 50000 });

  let response: AxiosResponse<any>;
  try {
    response = await refreshAxios.post(
      refreshUrl,
      { refreshToken, sessionId },
      { headers: { 'X-Message-Id': uuidv4(), 'X-Correlation-Id': uuidv4() } }
    );
  } catch (err: any) {
    console.error('[AUTH] Refresh HTTP call failed:', {
      status: err?.response?.status,
      data: err?.response?.data,
      code: err?.code,
      message: err?.message,
    });
    throw err;
  }

  const data = response.data?.data ?? response.data;
  const newAccessToken: string | undefined = data?.accessToken;
  const newRefreshToken: string | undefined = data?.refreshToken ?? refreshToken;

  if (!newAccessToken) {
    console.error('[AUTH] Refresh response missing accessToken. Full data:', data);
    throw new Error('Invalid refresh response: missing accessToken');
  }

  // Write to localStorage
  updatePersistUserDbAuthTokens({
    accessToken: newAccessToken,
    refreshToken: newRefreshToken ?? refreshToken,
    sessionId: sessionId ?? undefined,
  });

  // Also update Redux store to keep in-memory state in sync
  try {
    const currentState = store.getState() as {
      authDetails?: { auth?: { user?: Record<string, unknown> } };
    };
    store.dispatch({
      type: SET_AUTH_DATA,
      payload: {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken ?? refreshToken,
        sessionId: sessionId,
        user: currentState.authDetails?.auth?.user || {},
      },
    });
  } catch {
    // Non-critical — Redux update failure should not fail the refresh
  }

  return newAccessToken;
};

// ─── Shared queuing state ─────────────────────────────────────────────────────

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshFailSubscribers: Array<(err: unknown) => void> = [];

const subscribeTokenRefresh = (onSuccess: (token: string) => void, onFailure: (err: unknown) => void) => {
  refreshSubscribers.push(onSuccess);
  refreshFailSubscribers.push(onFailure);
};

const notifySubscribers = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshFailSubscribers = [];
};

const notifySubscribersFailed = (err: unknown) => {
  refreshFailSubscribers.forEach((cb) => cb(err));
  refreshSubscribers = [];
  refreshFailSubscribers = [];
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Adds Authorization, X-Message-Id, and X-Correlation-Id headers to request config.
 * If no access token is present the request is sent without an Authorization header;
 * the server will return 401 and the response interceptor will handle the refresh flow.
 */
export function addAuthHeader(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  const { accessToken } = getAccessAndRefreshToken();
  if (!accessToken || accessToken.trim() === '') {
    // Do not redirect here — the server will return 401 and the response interceptor will handle it.
    // Redirecting in the request interceptor fires before the response arrives and races with the
    // response-interceptor's refresh flow.
    console.warn('[AUTH] Request interceptor: no accessToken in store — sending without auth header', config.url);
    return config;
  }
  const tokenInfo = decodeTokenInfo(accessToken);
  console.log(
    `%c[AUTH] → ${config.method?.toUpperCase()} ${config.url}`,
    'color:#3498db',
    '| token expires:', tokenInfo.exp,
    '| isExpired:', tokenInfo.isExpired,
  );
  if (config.headers) {
    config.headers['X-Message-Id'] = uuidv4();
    config.headers['X-Correlation-Id'] = uuidv4();
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
}

/**
 * Handles 401 errors by refreshing token (up to MAX_REFRESH_ATTEMPTS) and retrying.
 * Queues concurrent 401'd requests while refresh is in progress.
 * Redirects to login if all refresh attempts fail.
 */
export async function handle401Error(
  error: AxiosError,
  retryRequest: (config: InternalAxiosRequestConfig) => Promise<AxiosResponse>
): Promise<AxiosResponse> {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (!originalRequest) {
    return Promise.reject(error);
  }

  // Skip refresh for the refresh endpoint itself
  if (originalRequest.url?.includes('token/refresh')) {
    console.error('[AUTH] Refresh endpoint failed — redirecting to login');
    redirectToLogin();
    return Promise.reject(error);
  }

  // If already retried, redirect to login
  if (originalRequest._retry) {
    console.error('[AUTH] Retried request still failing — redirecting to login');
    redirectToLogin();
    return Promise.reject(error);
  }

  const requestUrl = originalRequest.url ?? '(unknown)';

  if (isRefreshing) {
    console.log(`%c[AUTH] 401 on "${requestUrl}" — refresh in progress, queuing request`, 'color:#9b59b6');
    return new Promise<AxiosResponse>((resolve, reject) => {
      subscribeTokenRefresh(
        (newToken) => {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          }
          retryRequest(originalRequest).then(resolve).catch(reject);
        },
        (err) => {
          console.error(`[AUTH] Queued request "${requestUrl}" rejected — refresh failed`, err);
          reject(err);
        }
      );
    });
  }

  originalRequest._retry = true;
  console.log(`%c[AUTH] 401 on "${requestUrl}" — starting token refresh`, 'color:#e67e22;font-weight:bold');
  isRefreshing = true;
  let lastError: unknown = error;

  try {
    for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
      let newAccessToken: string;
      try {
        newAccessToken = await refreshAccessToken();
      } catch (err) {
        lastError = err;
        console.error(`[AUTH] Token refresh attempt ${attempt}/${MAX_REFRESH_ATTEMPTS} failed:`, err);
        continue;
      }

      console.log(`%c[AUTH] Refresh succeeded (attempt ${attempt}) — retrying "${requestUrl}"`, 'color:#27ae60;font-weight:bold');

      if (originalRequest.headers) {
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      }
      notifySubscribers(newAccessToken);

      try {
        const retryResponse = await retryRequest(originalRequest);
        console.log(`%c[AUTH] Retry succeeded for "${requestUrl}"`, 'color:#27ae60');
        return retryResponse;
      } catch (retryErr: any) {
        console.error(`[AUTH] Retry of "${requestUrl}" failed after successful refresh:`, retryErr);
        // Notify queued subscribers so they are not left pending indefinitely.
        notifySubscribersFailed(retryErr);
        throw retryErr;
      }
    }

    console.error('[AUTH] All refresh attempts exhausted — redirecting to login');
    notifySubscribersFailed(lastError);
    redirectToLogin();
    return Promise.reject(lastError);
  } finally {
    isRefreshing = false;
  }
}
